import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, currentPermission, isSubscribed, enablePush, disablePush } from "@/lib/push";

export default function NotificationButton({ compact = false }) {
  const [state, setState] = useState("loading"); // loading | unsupported | denied | off | on
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!pushSupported()) return setState("unsupported");
    const p = currentPermission();
    if (p === "denied") return setState("denied");
    const subbed = await isSubscribed();
    setState(subbed && p === "granted" ? "on" : "off");
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (state === "on") {
        await disablePush();
        toast.success("Notifiche disattivate");
      } else {
        await enablePush();
        toast.success("Notifiche attivate — riceverai avvisi anche a schermo spento");
      }
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Errore attivazione notifiche");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") return null;

  if (state === "unsupported") {
    return (
      <div className={`flex items-center gap-2 text-xs text-slate-400 ${compact ? "" : "px-3 py-2"}`} data-testid="push-unsupported">
        <BellOff className="w-4 h-4" /> Notifiche non supportate
      </div>
    );
  }

  const label =
    state === "on" ? "Notifiche attive" :
    state === "denied" ? "Notifiche bloccate" :
    "Attiva notifiche";

  const Icon = busy ? Loader2 : (state === "on" ? BellRing : Bell);
  const color =
    state === "on" ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30 hover:bg-emerald-500/25"
    : state === "denied" ? "bg-rose-500/10 text-rose-300 border-rose-500/30 cursor-not-allowed"
    : "bg-sky-500/15 text-sky-200 border-sky-500/30 hover:bg-sky-500/25";

  return (
    <button
      onClick={state === "denied" ? undefined : toggle}
      disabled={busy || state === "denied"}
      data-testid="push-toggle-button"
      title={state === "denied" ? "Sblocca le notifiche dalle impostazioni del browser" : label}
      className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition ${color}`}
    >
      <Icon className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
      {label}
    </button>
  );
}
